import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, useScroll, useTransform, useSpring, useMotionValueEvent, useInView } from 'framer-motion';

const Section = styled.section`
  padding: 100px 2rem;
  background: #000000;
  position: relative;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const Container = styled.div`
  max-width: 1350px;
  margin: 0 auto;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
  position: relative;
  height: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const LeftColumn = styled.div`
  position: sticky;
  top: 100px;
  height: fit-content;
  align-self: start;
  padding-right: 2rem;
  
  @media (max-width: 768px) {
    position: relative;
    top: auto;
    padding-right: 0;
  }
`;

const RightColumn = styled.div`
  position: relative;
  padding-left: 2rem;
  
  @media (max-width: 768px) {
    padding-left: 0;
  }
`;

const ServicesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ServiceItem = styled(motion.div)`
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    min-height: 100px;
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  color: #ffffff;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const ServiceDescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: #cbd5e1;
  margin-bottom: 1.5rem;
  max-width: 600px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  width: 100%;
`;

const Square = styled.div`
  width: 15px;
  height: 15px;
  background: #ffffff;
  flex-shrink: 0;
`;

const Line = styled.div`
  flex: 1;
  height: 1px;
  background: #ffffff;
  margin-left: 1rem;
`;

const Title = styled.h2`
  font-size: 18px;
  color: #ffffff;
  margin: 0;
  white-space: nowrap;
`;

const MainTitle = styled.h1`
  font-size: 24px;
  color: #ffffff;
  margin-bottom: 2rem;
  line-height: 1.1;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 50%;
  aspect-ratio: 4/3;
  border-radius: 8px;
  overflow: hidden;
  background: #1f2937;
  margin-top: 2rem;
`;

const ServiceImage = styled(motion.div)`
  position: absolute;
  inset: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImageGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6), transparent);
`;

const ScrollProgress = styled(motion.div)`
  position: fixed;
  right: 2rem;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 200px;
  background: #374151;
  border-radius: 2px;
  overflow: hidden;
  z-index: 1000;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ProgressFill = styled(motion.div)`
  width: 100%;
  background: linear-gradient(180deg, #3b82f6, #1d4ed8);
  border-radius: 2px;
`;

const ServicesSection = () => {
  const sectionRef = useRef(null);
  const servicesContainerRef = useRef(null);
  const serviceRefs = useRef([]);
  const isInView = useInView(sectionRef, { margin: "-50% 0px" });
  
  const [activeServiceIndex, setActiveServiceIndex] = useState(0);
  const [allServicesViewed, setAllServicesViewed] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);

  const { scrollYProgress } = useScroll({
    target: servicesContainerRef,
    offset: ["start end", "end start"]
  });

  // Only show scroll hint when section is in view
  useEffect(() => {
    if (isInView && !allServicesViewed) {
      setShowScrollHint(true);
    } else {
      setShowScrollHint(false);
    }
  }, [isInView, allServicesViewed]);

  // Track when user has scrolled through all services
  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    if (latest > 0.95 && !allServicesViewed) {
      setAllServicesViewed(true);
      setShowScrollHint(false);
    }
  });

  // Track which service is in view
  useEffect(() => {
    const observers = serviceRefs.current.map((ref, index) => {
      if (!ref) return null;
      
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveServiceIndex(index);
            }
          });
        },
        {
          threshold: 0.5,
          rootMargin: "-20% 0px -20% 0px"
        }
      );
      
      observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, []);

  // Spring-based progress for smoother animation
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const services = [
    {
      title: 'Recruitment Services',
      image: '/Lightbulbs.jpg'
    },
    {
      title: 'Temporary, Freelance, Fractional, Contract & Permanent',
      image: '/Lightbulbs.jpg'
    },
    {
      title: 'Executive & Contingency Search',
      image: '/Lightbulbs.jpg'
    },
    {
      title: 'Entry to Executive Team Builds',
      image: '/Lightbulbs.jpg'
    },
    {
      title: 'Special Projects & Consulting',
      image: '/Lightbulbs.jpg'
    },
    {
      title: 'Workshops & Mentoring',
      image: '/Lightbulbs.jpg'
    }
  ];

  return (
    <Section id="services" ref={sectionRef}>
      <Container>
        <ContentWrapper>
          {/* Left Column - Sticky */}
          <LeftColumn>
            <TitleContainer>
              <Square />
              <Title>Our Services</Title>
              <Line />
            </TitleContainer>
            
            {/* Dynamic Image Container */}
            <ImageContainer>
              {services.map((service, index) => (
                <ServiceImage
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ 
                    opacity: activeServiceIndex === index ? 1 : 0,
                    scale: activeServiceIndex === index ? 1 : 1.1
                  }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                  <img
                    src={service.image}
                    alt={service.title}
                  />
                  <ImageGradient />
                </ServiceImage>
              ))}
            </ImageContainer>
          </LeftColumn>

          {/* Right Column - Scrollable content */}
          <RightColumn>
            <ServicesContainer ref={servicesContainerRef}>
              {services.map((service, index) => (
                <ServiceItem
                  key={index}
                  ref={(el) => (serviceRefs.current[index] = el)}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false, margin: "-100px" }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                >
                  <ServiceTitle>{service.title}</ServiceTitle>
                  <ServiceDescription>{service.description}</ServiceDescription>
                </ServiceItem>
              ))}
            </ServicesContainer>
          </RightColumn>
        </ContentWrapper>
      </Container>

      {/* Scroll Progress Indicator - Only show when section is in view */}
      {isInView && (
        <ScrollProgress
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <ProgressFill style={{ scaleY: smoothProgress }} />
        </ScrollProgress>
      )}
    </Section>
  );
};

export default ServicesSection;